import { Button, Card, Icon, MoreActionsDropdown, OrganizationAvatar } from "@components/library";
import type { ExtendedDropdownOption } from "@components/library/Dropdowns/DropdownOption";
import toast from "@components/Toast";
import { COLORS, FONTS, SPACINGS, STYLES } from "@constants";
import { RootState } from "@redux/store";
import { solution_listing_path } from "@routes/routes";
import { formatPathForRouter } from "@routes/utils";
import { DiscoverSolutionListing } from "@tsTypes/discover_feed";
import { copyLinkToClipboard, truncate } from "@utils/textUtils";
import { MouseEvent, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useDiscoverAnalytics } from "src/views/Discover/hooks/useDiscoverAnalytics";
import styled from "styled-components";
import { buildSolutionTags, getSolutionListingNavigationState, TagType } from "./solutionCardUtils";

interface SolutionCardProps {
  solution: DiscoverSolutionListing;
  variant?: "discover" | "innovator";
  position?: number;
  fromLabel?: string;
  className?: string;
  onShare?: (solution: DiscoverSolutionListing) => void;
  onLearnMore?: (solution: DiscoverSolutionListing, position: number) => void;
  onEdit?: (solution: DiscoverSolutionListing) => void;
  onDelete?: (solution: DiscoverSolutionListing) => void;
}

const SolutionCard = ({
  solution,
  variant = "discover",
  position = 0,
  fromLabel,
  className,
  onShare,
  onLearnMore,
  onEdit,
  onDelete,
}: SolutionCardProps) => {
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const { trackShareListing, trackLearnMoreClick } = useDiscoverAnalytics();
  const tags = buildSolutionTags(solution);
  const descriptionText =
    variant === "innovator"
      ? truncateDescription(solution.summary, solution.description)
      : solution.summary;
  const solutionSlug = solution.slug ?? solution.external_id;

  const computedMoreActionsOptions = useMemo((): ExtendedDropdownOption[] => {
    const options: ExtendedDropdownOption[] = [];

    if (onEdit) {
      options.push({
        value: "EDIT",
        label: "Edit",
        iconName: "Pencil",
        onSelect: () => onEdit(solution),
      });
    }

    if (onDelete) {
      options.push({
        value: "DELETE",
        label: "Delete",
        iconName: "Delete",
        optionColor: COLORS.RED,
        onSelect: () => onDelete(solution),
      });
    }

    return options;
  }, [onEdit, onDelete, solution]);

  const editActions =
    variant === "innovator" && computedMoreActionsOptions.length > 0 ? (
      <MoreActionsWrapper onClick={(e: MouseEvent<HTMLDivElement>) => e.preventDefault()}>
        <MoreActionsDropdown
          options={computedMoreActionsOptions}
          menuHorizontalPosition="right"
          menuWidth="125px"
        >
          <MoreActionsButton type="button" aria-label="More actions">
            <Icon name="More Actions" size="xs" />
          </MoreActionsButton>
        </MoreActionsDropdown>
      </MoreActionsWrapper>
    ) : null;

  const handleShare = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    trackShareListing({
      solution_listing_id: solution.external_id,
    });

    if (onShare) {
      onShare(solution);
    } else {
      try {
        const url = new URL(solution_listing_path(solutionSlug), window.location.origin).toString();
        await copyLinkToClipboard(currentUser.id, url);
        toast.success("Link copied to clipboard");
      } catch (e) {
        toast.error("Failed to copy link");
      }
    }
  };

  const handleLearnMore = () => {
    trackLearnMoreClick({
      solution_listing_id: solution.external_id,
      listing_position: position,
    });

    if (onLearnMore) {
      onLearnMore(solution, position);
    }
  };

  const linkTo = fromLabel
    ? getSolutionListingNavigationState(solutionSlug, fromLabel)
    : formatPathForRouter(solution_listing_path(solutionSlug));

  const content = (
    <>
      <HeaderSection $variant={variant}>
        {variant === "innovator" && (
          <LogoContainer>
            <OrganizationAvatar
              imageUrl={solution.organization?.logo_url ?? solution.image_url ?? undefined}
              size="lg"
            />
          </LogoContainer>
        )}
        <OrganizationInfo $hasLogo={variant === "innovator"} $variant={variant}>
          <OrganizationName>
            <OrganizationNameText>
              {solution.organization?.name ?? "Organization"}
            </OrganizationNameText>
            {variant === "innovator" && editActions}
          </OrganizationName>
        </OrganizationInfo>
      </HeaderSection>

      <DetailsSection $variant={variant}>
        <ContentWrapper $variant={variant}>
          <Title $variant={variant}>{solution.title}</Title>
          {variant === "innovator" && descriptionText && (
            <Description $variant={variant}>{descriptionText}</Description>
          )}
          {variant === "discover" && (
            <Description $variant={variant}>{solution.summary}</Description>
          )}
        </ContentWrapper>

        {variant === "discover" && (
          <RelevanceSection>
            <RelevanceChip>
              <Icon name="Magic Full" size="xs" />
              <RelevanceLabel>Relevance</RelevanceLabel>
            </RelevanceChip>
            <RelevanceText>
              {solution.prediction?.extra_data?.relevance_reason ?? "No summary yet"}
            </RelevanceText>
          </RelevanceSection>
        )}
      </DetailsSection>

      <TagsSection $variant={variant}>
        {tags.map((tag) => (
          <Tag key={tag.label} $type={tag.type}>
            <TagLabel>{tag.label}</TagLabel>
          </Tag>
        ))}
      </TagsSection>

      <FooterSection $variant={variant}>
        <ButtonGroup>
          <Button
            variant="outline"
            size="sm"
            leftIconName="Share"
            iconColor={COLORS.NEUTRAL_600}
            onClick={handleShare}
          >
            Share
          </Button>
          <Button variant="outline" size="sm" onClick={handleLearnMore}>
            Learn more
          </Button>
        </ButtonGroup>
      </FooterSection>
    </>
  );

  if (variant === "discover") {
    return (
      <Card padding="24px 0 16px 0" data-entity-id={solution.id} data-entity-type="solution">
        <CardLink to={linkTo} onClick={handleLearnMore} className={className}>
          {content}
        </CardLink>
      </Card>
    );
  }

  return (
    <CardContainer
      to={linkTo}
      className={className}
      data-entity-type="solution"
      data-entity-id={solution.id}
      onClick={handleLearnMore}
    >
      {content}
    </CardContainer>
  );
};

export default SolutionCard;

const DESCRIPTION_CHARACTER_LIMIT = 220;

function truncateDescription(
  summary: string | null | undefined,
  description: string | null | undefined
): string | null {
  const text = summary?.trim() ?? description?.trim();
  if (!text) return null;
  return truncate(text, DESCRIPTION_CHARACTER_LIMIT);
}

const CardLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  &:hover {
    text-decoration: none;
  }
`;

const CardContainer = styled(Link)`
  background-color: ${COLORS.WHITE};
  width: 100%;
  height: 100%;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  border-radius: 4px;
  border-width: 1px;
  border-color: ${COLORS.NEUTRAL_250};
  border-style: solid;
  box-shadow: ${STYLES.SHADOW_CARD};
  transition: box-shadow 0.2s ease;
  color: inherit;
  text-decoration: none;

  &:hover {
    box-shadow: ${STYLES.SHADOW_A};
    text-decoration: none;
    color: inherit;
  }
`;

const HeaderSection = styled.div<{ $variant: "discover" | "innovator" }>`
  display: flex;
  align-items: center;
  ${({ $variant }) =>
    $variant === "discover"
      ? `
        gap: 8px;
        padding: 0 24px;
      `
      : `
        align-items: center;
        padding: ${SPACINGS.SIZE_4} ${SPACINGS.SIZE_6} ${SPACINGS.SIZE_8} ${SPACINGS.SIZE_6};
        gap: ${SPACINGS.SIZE_4};
        align-self: stretch;
      `}
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.SIZE_4};
`;

const OrganizationInfo = styled.div<{
  $hasLogo: boolean;
  $variant: "discover" | "innovator";
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  ${({ $variant, $hasLogo }) =>
    $variant === "innovator"
      ? `
        width: ${$hasLogo ? "260px" : "auto"};
        gap: ${SPACINGS.SIZE_1};
        flex-grow: 1;
        flex-shrink: 0;
      `
      : `
        flex-grow: 1;
      `}
`;

const OrganizationName = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACINGS.SIZE_2};
  flex-shrink: 0;
  flex-grow: 1;
  align-self: stretch;
`;

const OrganizationNameText = styled.span`
  ${FONTS.SEMIBOLD_1}
  color: ${COLORS.NEUTRAL_800};
`;

const DetailsSection = styled.div<{ $variant: "discover" | "innovator" }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${({ $variant }) =>
    $variant === "discover"
      ? `
        padding: 32px 24px 16px 24px;
        height: 376px;
        gap: 24px;
      `
      : `
        gap: ${SPACINGS.SIZE_6};
        padding: 0 ${SPACINGS.SIZE_6} ${SPACINGS.SIZE_8};
        align-self: stretch;
        flex-grow: 1;
      `}
`;

const ContentWrapper = styled.div<{ $variant: "discover" | "innovator" }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${({ $variant }) =>
    $variant === "discover"
      ? `
        gap: 16px;
      `
      : `
        gap: ${SPACINGS.SIZE_2};
        align-self: stretch;
      `}
`;

const Title = styled.h3<{ $variant: "discover" | "innovator" }>`
  ${FONTS.MEDIUM_1}
  color: ${COLORS.NEUTRAL_800};
  margin: 0;
  ${({ $variant }) => $variant === "innovator" && `${STYLES.TWO_LINES}`}
`;

const Description = styled.p<{ $variant: "discover" | "innovator" }>`
  ${FONTS.MEDIUM_2}
  color: ${COLORS.NEUTRAL_500};
  margin: 0;
  ${({ $variant }) =>
    $variant === "discover" &&
    `
      ${STYLES.THREE_LINES}
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;

const RelevanceSection = styled.div`
  background: ${COLORS.NEUTRAL_50};
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;
`;

const RelevanceChip = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  background: white;
  border-radius: 4px;
  padding: 0 6px;
  width: fit-content;
`;

const RelevanceLabel = styled.span`
  ${FONTS.MEDIUM_3}
  color: ${COLORS.NEUTRAL_800};
`;

const RelevanceText = styled.p`
  ${FONTS.MEDIUM_2}
  color: ${COLORS.NEUTRAL_600};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TagsSection = styled.div<{ $variant: "discover" | "innovator" }>`
  display: flex;
  align-items: center;
  gap: ${({ $variant }) => ($variant === "discover" ? "8px" : SPACINGS.SIZE_2)};
  flex-wrap: wrap;
  ${({ $variant }) =>
    $variant === "discover"
      ? `
    padding: 0 24px 16px 24px;
  `
      : `
    padding: 0 ${SPACINGS.SIZE_6} ${SPACINGS.SIZE_4};
    flex-shrink: 0;
    align-self: stretch;
  `}
`;

const Tag = styled.div<{ $type: TagType }>`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 8px;
  border-radius: 100px;
  background: ${(props) => {
    switch (props.$type) {
      case TagType.SOLUTION:
        return COLORS.BLUE_LIGHT_300;
      case TagType.INSTITUTION:
        return COLORS.INDIGO_100;
      case TagType.LISTING_TYPE:
        // These greens are temporarily hardcoded until we transition to the new design system color scheme
        return "#EAF7F4"; // Minty color matching marketplace partnering model tags
      default:
        return COLORS.NEUTRAL_100;
    }
  }};
  color: ${(props) => {
    switch (props.$type) {
      case TagType.SOLUTION:
        return COLORS.BLUE_500;
      case TagType.INSTITUTION:
        return COLORS.INDIGO_800;
      case TagType.LISTING_TYPE:
        // These greens are temporarily hardcoded until we transition to the new design system color scheme
        return "#0F9158"; // Minty color matching marketplace partnering model tags
      default:
        return COLORS.NEUTRAL_600;
    }
  }};
`;

const TagLabel = styled.span`
  ${FONTS.TAG_MEDIUM_2}
`;

const FooterSection = styled.div<{ $variant: "discover" | "innovator" }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $variant }) => ($variant === "discover" ? "end" : "space-between")};
  gap: ${({ $variant }) => ($variant === "discover" ? "8px" : SPACINGS.SIZE_4)};
  border-width: 1px 0 0;
  border-color: ${COLORS.NEUTRAL_200};
  border-style: solid;
  ${({ $variant }) =>
    $variant === "discover"
      ? `
    padding: 16px 24px 0 24px;
    position: relative;
    margin-top: auto;
  `
      : `
    padding: ${SPACINGS.SIZE_4} ${SPACINGS.SIZE_6};
    flex-shrink: 0;
    align-self: stretch;
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.SIZE_2};
  justify-content: flex-end;
  flex-grow: 1;
`;

const MoreActionsWrapper = styled.div`
  .Select__control--menu-is-open button {
    background-color: ${COLORS.NEUTRAL_100};
  }
`;

const MoreActionsButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${COLORS.NEUTRAL_250};
  background-color: ${COLORS.NEUTRAL_0};
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
